import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, ClipboardList, User, Layers } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import {
  hasConfigurableLimit,
  buildModuleLimitsPayload,
  moduleLimitsToInputs,
  formatModuleLimitLabel
} from '../../utils/moduleLimits'

export default function EditPlanModal({ isOpen, onClose, plan, productId, modules = [], onSuccess }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedModules, setSelectedModules] = useState(new Set())
  const [moduleLimitInputs, setModuleLimitInputs] = useState({})

  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      plan_name: '',
      description: '',
      monthly_price: '0.00',
      yearly_price: '0.00',
      currency: 'INR',
      max_users: '',
      trial_days: 0,
      display_order: 0,
      is_popular: false,
      is_enterprise: false
    }
  })

  const isEnterprise = watch('is_enterprise')
  const currency = watch('currency')

  useEffect(() => {
    if (!plan) return
    const planFeatures = plan.features || {}
    setValue('plan_name', plan.name || '')
    setValue('description', plan.description || '')
    setValue('monthly_price', plan.monthly_price ?? plan.price ?? '0.00')
    setValue('yearly_price', plan.yearly_price ?? plan.price ?? '0.00')
    setValue('currency', plan.currency || 'INR')
    setValue('max_users', plan.max_users ?? planFeatures.max_users ?? '')
    setValue('trial_days', plan.trial_days || 0)
    setValue('display_order', plan.display_order || 0)
    setValue('is_popular', plan.is_popular || false)
    setValue('is_enterprise', plan.is_enterprise || false)

    const moduleIds = plan.module_ids || plan.included_modules || []
    setSelectedModules(new Set(moduleIds))
    setModuleLimitInputs(moduleLimitsToInputs(plan.module_limits || {}, modules))
  }, [plan, modules, setValue])

  useEffect(() => {
    if (!plan) return
    if (isEnterprise) {
      setSelectedModules(new Set(modules.map((m) => m.id)))
    } else {
      setSelectedModules(new Set(plan.module_ids || plan.included_modules || []))
      setModuleLimitInputs(moduleLimitsToInputs(plan.module_limits || {}, modules))
    }
  }, [isEnterprise, modules, plan])

  const currencySymbol =
    { USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'C$', AUD: 'A$', JPY: '¥' }[currency] || '$'

  const handleSelectAllModules = () => {
    setSelectedModules(new Set(modules.map((m) => m.id)))
  }

  const handleClearAllModules = () => {
    setSelectedModules(new Set())
    setModuleLimitInputs({})
  }

  const handleModuleToggle = (moduleId) => {
    if (isEnterprise) return
    const next = new Set(selectedModules)
    if (next.has(moduleId)) {
      next.delete(moduleId)
      setModuleLimitInputs((prev) => {
        const copy = { ...prev }
        delete copy[moduleId]
        return copy
      })
    } else {
      next.add(moduleId)
    }
    setSelectedModules(next)
  }

  const handleModuleLimitChange = (moduleId, field, value) => {
    setModuleLimitInputs((prev) => ({
      ...prev,
      [moduleId]: {
        value: '',
        storageUnit: 'GB',
        ...prev[moduleId],
        [field]: value
      }
    }))
  }

  const mutation = useMutation(
    (planData) => api.put(`/plans/${plan.id}`, planData).then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['plans', productId])
        toast.success('Plan updated successfully!')
        reset()
        setSelectedModules(new Set())
        setModuleLimitInputs({})
        onClose()
        if (onSuccess) onSuccess()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update plan')
      }
    }
  )

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const moduleIds = Array.from(selectedModules)
      await mutation.mutateAsync({
        name: data.plan_name,
        description: data.description || '',
        monthly_price: parseFloat(data.monthly_price) || 0,
        yearly_price: parseFloat(data.yearly_price) || 0,
        currency: data.currency || 'INR',
        trial_days: parseInt(data.trial_days, 10) || 0,
        features: {
          max_users: data.max_users ? parseInt(data.max_users, 10) : null,
          max_storage_gb: null,
          api_requests_per_month: null,
          support_level: 'standard'
        },
        is_popular: Boolean(data.is_popular),
        is_enterprise: Boolean(data.is_enterprise),
        display_order: parseInt(data.display_order, 10) || 0,
        module_ids: moduleIds,
        feature_ids: [],
        module_limits: buildModuleLimitsPayload(moduleIds, modules, moduleLimitInputs)
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    reset()
    setSelectedModules(new Set())
    setModuleLimitInputs({})
    onClose()
  }

  if (!isOpen || !plan) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between shrink-0 border-b border-secondary-100 pb-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <CardTitle>Edit plan</CardTitle>
              <p className="text-xs text-secondary-500 mt-0.5">
                Update pricing, seat limit, and included modules
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={isSubmitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="overflow-y-auto flex-1 pt-5">
          <form id="edit-plan-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Basics
              </h3>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Plan name *
                </label>
                <input
                  {...register('plan_name', {
                    required: 'Plan name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' }
                  })}
                  type="text"
                  className="input w-full"
                  disabled={isSubmitting}
                />
                {errors.plan_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.plan_name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={2}
                  className="input w-full resize-none"
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-secondary-700">
                  <input
                    {...register('is_popular')}
                    type="checkbox"
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  Popular plan
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-secondary-700">
                  <input
                    {...register('is_enterprise')}
                    type="checkbox"
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  Enterprise (all modules, custom pricing)
                </label>
              </div>
            </section>

            <section className="space-y-4 border-t border-secondary-100 pt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Pricing
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Monthly
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">
                      {currencySymbol}
                    </span>
                    <input
                      {...register('monthly_price')}
                      type="number"
                      step="0.01"
                      min="0"
                      className="input w-full pl-8"
                      disabled={isSubmitting || isEnterprise}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Yearly
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm">
                      {currencySymbol}
                    </span>
                    <input
                      {...register('yearly_price')}
                      type="number"
                      step="0.01"
                      min="0"
                      className="input w-full pl-8"
                      disabled={isSubmitting || isEnterprise}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Currency
                  </label>
                  <select {...register('currency')} className="input w-full" disabled={isSubmitting}>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                    <option value="JPY">JPY</option>
                  </select>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-secondary-100 pt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Limits & display
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Max users
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      {...register('max_users')}
                      type="text"
                      className="input w-full pl-10"
                      placeholder="Unlimited"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Trial days
                  </label>
                  <input
                    {...register('trial_days')}
                    type="number"
                    min="0"
                    className="input w-full"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Display order
                  </label>
                  <input
                    {...register('display_order')}
                    type="number"
                    min="0"
                    className="input w-full"
                    disabled={isSubmitting}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3 border-t border-secondary-100 pt-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-secondary-500" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    Included modules
                  </h3>
                  <span className="text-xs text-secondary-500">
                    {selectedModules.size} selected
                  </span>
                </div>
                {modules.length > 0 && !isEnterprise && (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleSelectAllModules} disabled={isSubmitting}>
                      Select all
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handleClearAllModules} disabled={isSubmitting}>
                      Clear
                    </Button>
                  </div>
                )}
              </div>

              {modules.length > 0 ? (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-secondary-200 divide-y divide-secondary-100">
                  {modules.map((module) => {
                    const selected = selectedModules.has(module.id)
                    const showLimit = selected && hasConfigurableLimit(module)
                    const limitInput = moduleLimitInputs[module.id] || { value: '', storageUnit: 'GB' }
                    const indent = Math.min(Number(module.level) || 0, 4) * 14

                    return (
                      <div
                        key={module.id}
                        className={`px-3 py-2.5 ${selected ? 'bg-primary-50/40' : 'bg-white'} hover:bg-secondary-50`}
                        style={{ paddingLeft: 12 + indent }}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => handleModuleToggle(module.id)}
                            className="mt-1 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                            disabled={isSubmitting || isEnterprise}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-1.5">
                              <span className="text-sm font-medium text-secondary-900">{module.name}</span>
                              {module.type === 'required' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                                  Required
                                </span>
                              )}
                              {hasConfigurableLimit(module) && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                  {module.limit_type}
                                  {module.limit_unit ? ` · ${module.limit_unit}` : ''}
                                </span>
                              )}
                            </div>
                            {module.description && (
                              <p className="text-xs text-secondary-500 mt-0.5 line-clamp-2">
                                {module.description}
                              </p>
                            )}
                          </div>
                        </label>
                        {showLimit && (
                          <div className="mt-2 ml-7 flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-secondary-600 whitespace-nowrap">
                              {formatModuleLimitLabel(module)}
                            </span>
                            <input
                              type="number"
                              min="0"
                              step={module.limit_type === 'storage' ? '0.001' : '1'}
                              className="input w-28 py-1 text-sm"
                              placeholder="Unlimited"
                              value={limitInput.value}
                              disabled={isSubmitting}
                              onChange={(e) => handleModuleLimitChange(module.id, 'value', e.target.value)}
                            />
                            {module.limit_type === 'storage' ? (
                              <select
                                className="input w-20 py-1 text-sm"
                                value={limitInput.storageUnit || 'GB'}
                                disabled={isSubmitting}
                                onChange={(e) => handleModuleLimitChange(module.id, 'storageUnit', e.target.value)}
                              >
                                <option value="MB">MB</option>
                                <option value="GB">GB</option>
                              </select>
                            ) : (
                              <span className="text-xs text-secondary-500">{module.limit_unit}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-secondary-200 p-6 text-center">
                  <p className="text-sm text-secondary-600">
                    No modules available. Load modules from your product API in Config first.
                  </p>
                </div>
              )}
            </section>
          </form>
        </CardContent>

        <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50/50">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="edit-plan-form" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Save changes'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
